import { __read, __spread, __values } from "tslib";
import { useCallback, useEffect, useLayoutEffect, useState, useRef, } from 'react';
import { scrollToElem, findPrevPicked, findNextPicked, findIndexFromTail, addChoicesSubset, setChoicesSubset, delChoicesSubset, hasChoicesSubset, isChoicesMapsEqual, eventHandler, } from '../ToolsSuggest.utils';
/**
 * Хук для работы ToolsSuggest.
 * Вынесено для чистоты кода компонента.
 */
export function useToolsSuggest(props) {
    var _a = props.choices, choices = _a === void 0 ? [] : _a, _b = props.chosen, chosen = _b === void 0 ? [] : _b, _c = props.maxChosen, maxChosen = _c === void 0 ? Infinity : _c, _d = props.opened, opened = _d === void 0 ? false : _d, _e = props.picked, picked = _e === void 0 ? [] : _e, _f = props.value, value = _f === void 0 ? '' : _f, _g = props.chosenPlace, chosenPlace = _g === void 0 ? 'inline' : _g, restorableChosen = props.restorableChosen, _h = props.showPopupOnEmptyChosen, showPopupOnEmptyChosen = _h === void 0 ? true : _h, _j = props.showPopupOnInputFocus, showPopupOnInputFocus = _j === void 0 ? true : _j, _k = props.focusInputOnWrapperClick, focusInputOnWrapperClick = _k === void 0 ? true : _k, _l = props.scrollToControlOnFocus, scrollToControlOnFocus = _l === void 0 ? true : _l, buttonValue = props.buttonValue, _m = props.onChoicesScroll, onChoicesScroll = _m === void 0 ? eventHandler : _m, _o = props.onChosenChange, onChosenChange = _o === void 0 ? eventHandler : _o, _p = props.onOpenedChange, onOpenedChange = _p === void 0 ? eventHandler : _p, _q = props.onPickedChange, onPickedChange = _q === void 0 ? eventHandler : _q, _r = props.onValueChange, onValueChange = _r === void 0 ? eventHandler : _r, createExcludingGroup = props.createExcludingGroup, onBlur = props.onBlur, onKeyDown = props.onKeyDown, onFocus = props.onFocus, onChange = props.onChange;
    /** Ref текстового поля. Используется, если не передан внешний. */
    var controlRefInner = useRef(null);
    /** Ref всплывающего окна. Используется, если не передан внешний. */
    var popupRefInner = useRef(null);
    /** Ref для позиционирования модального окна. */
    var anchorRef = useRef(null);
    /** Ref списка пунктов саджеста в попапе. */
    var listRef = useRef(null);
    var controlRef = props.controlRef || controlRefInner;
    var popupRef = props.popupRef || popupRefInner;
    /**
     * Хранение данных
     */
    var _s = __read(useState(false), 2), focused = _s[0], setFocused = _s[1];
    var _t = __read(useState(new Map()), 2), removedChosenItems = _t[0], setRemovedChosenItems = _t[1];
    /**
     * Общие команды
     */
    /** Найти пункт в списке в попапе */
    var findChoice = useCallback(function (_a) {
        var id = _a.id;
        var current = listRef.current;
        if (!current) {
            return null;
        }
        return current.querySelector("[data-id=\"" + 
        // Экранирую значение атрибута, чтобы не сломать селектор
        String(id).replace(/["\[\]]/, '\\&') + "\"]");
    }, [listRef]);
    /**
     * Команды для управления прокуткой
     */
    /** Прокрутка к текстовому полю */
    var scrollToSuggest = useCallback(function () {
        scrollToElem(controlRef.current);
    }, [controlRef]);
    /** Прокрутка к последнему выделенному элементу */
    var scrollToLastPicked = useCallback(function () {
        if (!picked || picked.length === 0) {
            return;
        }
        scrollToElem(findChoice(picked[picked.length - 1]));
    }, [picked, findChoice]);
    /**
     * Управлению фокусом в текстовом поле
     */
    /** Выставить фокус на текстовое поле */
    var focusInput = useCallback(function () {
        var controlElem = controlRef.current;
        if (controlElem) {
            controlElem.focus();
        }
    }, [controlRef]);
    /** Убрать фокус из текстового поля */
    var blurInput = useCallback(function () {
        var controlElem = controlRef.current;
        if (controlElem) {
            controlElem.blur();
        }
    }, [controlRef]);
    /**
     * Изменение текста в поле
     */
    /** Обработчик изменения текта в поле */
    var changeValue = useCallback(function (newValue) {
        if (newValue === value) {
            return false;
        }
        onValueChange(newValue);
        return true;
    }, [value, onValueChange]);
    /**
     * Изменение выделенных значений в списке
     */
    /** Обработчик изменения выделенных значения */
    var changePicked = useCallback(function (subset) {
        if (picked === subset) {
            return;
        }
        onPickedChange(subset);
    }, [picked, onPickedChange]);
    /** Установить список выделенных значений целиком */
    var setPicked = useCallback(function (subset) {
        changePicked(setChoicesSubset(picked, subset, maxChosen));
    }, [changePicked, picked, maxChosen]);
    /** Добавить значения к списку выделенных значений */
    var addPicked = useCallback(function (subset) {
        changePicked(addChoicesSubset(picked, subset, maxChosen));
    }, [changePicked, picked, maxChosen]);
    /** Удалить из списка выделенных значений */
    var removePicked = useCallback(function (subset) {
        changePicked(delChoicesSubset(picked, subset, maxChosen));
    }, [changePicked, picked, maxChosen]);
    /**
     * Управление видимостью всплывающего окна
     */
    /** Обработчик переключение видимости всплывающего окна */
    var changeOpened = useCallback(function (value) {
        if (value === opened) {
            return false;
        }
        onOpenedChange(value);
        return true;
    }, [opened, onOpenedChange]);
    /** Открыть всплывающее окно */
    var openPopup = useCallback(function () {
        changeOpened(true);
        focusInput();
    }, [changeOpened, focusInput]);
    /** Скрыть всплывающее окно */
    var closePopup = useCallback(function () {
        if (changeOpened(false)) {
            setPicked([]);
        }
    }, [changeOpened, setPicked]);
    /**
     * Управление значением поля
     */
    /** Обработчик изменения выбранных значений */
    var changeChosen = useCallback(function (subset) {
        if (chosen === subset) {
            return;
        }
        changeValue('');
        onChosenChange(subset);
        if (subset.length === maxChosen) {
            blurInput();
        }
        else if (showPopupOnEmptyChosen && subset.length === 0) {
            openPopup();
        }
    }, [
        chosen, changeValue, onChosenChange, maxChosen,
        showPopupOnEmptyChosen, blurInput, openPopup,
    ]);
    /** Удаляет пункты из списка удалённых */
    var removeSubsetFromRestorableItems = useCallback(function (subset) {
        var e_1, _a;
        var subsetIds = new Set(subset.map(function (_a) {
            var id = _a.id;
            return id;
        }));
        var newRemovedChosenItems = new Map(removedChosenItems);
        try {
            for (var newRemovedChosenItems_1 = __values(newRemovedChosenItems), newRemovedChosenItems_1_1 = newRemovedChosenItems_1.next(); !newRemovedChosenItems_1_1.done; newRemovedChosenItems_1_1 = newRemovedChosenItems_1.next()) {
                var _b = __read(newRemovedChosenItems_1_1.value, 2), key = _b[0], item = _b[1];
                if (subsetIds.has(item.id)) {
                    newRemovedChosenItems.delete(key);
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (newRemovedChosenItems_1_1 && !newRemovedChosenItems_1_1.done && (_a = newRemovedChosenItems_1.return)) _a.call(newRemovedChosenItems_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        setRemovedChosenItems(newRemovedChosenItems);
    }, [removedChosenItems]);
    /** Изменить список выбранных значений */
    var setChosen = useCallback(function (subset) {
        removeSubsetFromRestorableItems(subset);
        changeChosen(setChoicesSubset(chosen, subset, maxChosen));
    }, [removeSubsetFromRestorableItems, changeChosen, chosen, maxChosen]);
    /** Добавить к списку выбранных значений */
    var addChosen = useCallback(function (subset) {
        removeSubsetFromRestorableItems(subset);
        changeChosen(addChoicesSubset(chosen, subset, maxChosen));
    }, [removeSubsetFromRestorableItems, changeChosen, chosen, maxChosen]);
    /** Удалить из списка выбранных значений */
    var removeChosen = useCallback(function (subset) {
        changeChosen(delChoicesSubset(chosen, subset, maxChosen));
    }, [chosen, maxChosen, changeChosen]);
    /** Возвращает список выбранных значений с удалёнными пунктами */
    var getChosenWithRemoved = useCallback(function () {
        var realChosenItems = setChoicesSubset(chosen, chosen, maxChosen);
        var chosenWithRemovedLength = realChosenItems.length + removedChosenItems.size;
        var chosenWithRemoved = new Array(chosenWithRemovedLength);
        var chosenIndex = 0;
        for (var i = 0; i < chosenWithRemovedLength; i++) {
            if (removedChosenItems.has(i)) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                chosenWithRemoved[i] = removedChosenItems.get(i);
            }
            else {
                chosenWithRemoved[i] = realChosenItems[chosenIndex];
                chosenIndex++;
            }
        }
        return chosenWithRemoved.filter(Boolean);
    }, [chosen, maxChosen, removedChosenItems]);
    /** Помечает как удалённый пункт в списке выбранных значений */
    var markToRemoveChosen = useCallback(function (subset, indexes) {
        var newRemovedChosenItems = new Map(removedChosenItems);
        for (var i = 0; i < subset.length; i++) {
            var item = subset[i];
            var index = indexes[i];
            newRemovedChosenItems.set(index, item);
        }
        if (isChoicesMapsEqual(removedChosenItems, newRemovedChosenItems)) {
            return;
        }
        setRemovedChosenItems(newRemovedChosenItems);
    }, [removedChosenItems]);
    /** Снимает с пункта в списке выбранных значений пометку об удалении */
    var restoreChosen = useCallback(function (subset, indexes) {
        var e_2, _a;
        var removedIds = new Set(__spread(removedChosenItems.values()).map(function (_a) {
            var id = _a.id;
            return id;
        }));
        var restoredIndexes = new Set(indexes);
        var chosenWithRemoved = getChosenWithRemoved();
        for (var i = 0; i < chosenWithRemoved.length; i++) {
            var item = chosenWithRemoved[i];
            if (item && removedIds.has(item.id) && !restoredIndexes.has(i)) {
                chosenWithRemoved[i] = undefined;
            }
        }
        var newRemovedChosenItems = new Map(removedChosenItems);
        if (createExcludingGroup) {
            // Если есть исключающие группы, надо удалить основываясь не на порядке, а с приоритетом восстановленных
            var groups = new Set(subset.map(function (item) { return createExcludingGroup(item); }).filter(Boolean));
            for (var i = 0; i < chosenWithRemoved.length; i++) {
                var item = chosenWithRemoved[i];
                if (!item) {
                    continue;
                }
                var key = createExcludingGroup(item);
                if (groups.has(key) && !restoredIndexes.has(i) && !newRemovedChosenItems.has(i)) {
                    chosenWithRemoved[i] = undefined;
                    newRemovedChosenItems.set(i, item);
                }
            }
        }
        var newSubset = chosenWithRemoved.filter(function (item) { return item !== undefined; });
        changeChosen(setChoicesSubset(chosen, newSubset, maxChosen));
        try {
            for (var indexes_1 = __values(indexes), indexes_1_1 = indexes_1.next(); !indexes_1_1.done; indexes_1_1 = indexes_1.next()) {
                var index = indexes_1_1.value;
                newRemovedChosenItems.delete(index);
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (indexes_1_1 && !indexes_1_1.done && (_a = indexes_1.return)) _a.call(indexes_1);
            }
            finally { if (e_2) throw e_2.error; }
        }
        setRemovedChosenItems(newRemovedChosenItems);
    }, [removedChosenItems, getChosenWithRemoved, createExcludingGroup, changeChosen, chosen, maxChosen]);
    /** Фильтрует элементы из одинаковых групп */
    var filterExcludingGroups = useCallback(function (subset) {
        if (!createExcludingGroup) {
            return;
        }
        var groups = new Map();
        var removeItems = [];
        var removeIndexes = [];
        for (var i = 0; i < subset.length; i++) {
            var item = subset[i];
            if (!item) {
                continue;
            }
            var key = createExcludingGroup(item);
            if (key == null) {
                continue;
            }
            if (groups.has(key)) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                var index = groups.get(key);
                removeIndexes.push(index);
                removeItems.push(subset[index]);
            }
            groups.set(key, i);
        }
        if (removeItems.length !== 0) {
            if (restorableChosen) {
                markToRemoveChosen(removeItems, removeIndexes);
            }
            removeChosen(removeItems);
        }
    }, [createExcludingGroup, restorableChosen, markToRemoveChosen, removeChosen]);
    var isIndexInRemovedChosenItems = useCallback(function (index) { return removedChosenItems.has(index); }, [removedChosenItems]);
    useEffect(function () {
        //  Из picked нужно удалить все те элементы, которых нет в choices
        setPicked(picked.filter(function (choice) { return findIndexFromTail(choices, choice) > -1; }));
    }, [picked, choices, setPicked]);
    useEffect(function () {
        //  Удаляем из выбранных пунктов те, что принадлежат одной группе
        createExcludingGroup && filterExcludingGroups(chosen);
    }, [chosen, createExcludingGroup, filterExcludingGroups]);
    useLayoutEffect(function () {
        scrollToLastPicked();
    }, [picked, scrollToLastPicked]);
    useLayoutEffect(function () {
        if (scrollToControlOnFocus && focused) {
            scrollToSuggest();
        }
    }, [focused, chosen, scrollToSuggest, scrollToControlOnFocus]);
    /** Обработчик фокуса в текстовое поле */
    var handleInputFocus = useCallback(function (event) {
        setFocused(true);
        if (showPopupOnInputFocus) {
            openPopup();
        }
        if (typeof onFocus === 'function') {
            onFocus(event);
        }
    }, [onFocus, setFocused, openPopup, showPopupOnInputFocus]);
    /** Обработчик потери фокуса в текстовом поле */
    var handleInputBlur = useCallback(function (event) {
        closePopup();
        setFocused(false);
        if (typeof onBlur === 'function') {
            onBlur(event);
        }
    }, [onBlur, setFocused, closePopup]);
    /** Обработчик изменения в текстовом поле */
    var handleInputChange = useCallback(function (event) {
        changeValue(event.target.value);
        if (value !== event.target.value) {
            openPopup();
        }
        if (typeof onChange === 'function') {
            onChange(event);
        }
    }, [value, onChange, openPopup, changeValue]);
    /** Обработчик нажатия мышкой в текстовое поле */
    var handleInputMouseDown = useCallback(function () {
        openPopup();
    }, [openPopup]);
    /**
     * Обработчик нажатия в поле саджеста.
     * Используется для фокусировки на текстовом поле при нажатии в другие элементы.
     */
    var handleInputWrapperMouseDown = useCallback(function (event) {
        if (!focusInputOnWrapperClick) {
            return;
        }
        // Keep focus
        if (event.target !== controlRef.current) {
            event.preventDefault();
        }
        focusInput();
    }, [controlRef, focusInput, focusInputOnWrapperClick]);
    /** Обработчик нажатия горячих клавиш в поле */
    var handleInputKeyDown = useCallback(function (event) {
        switch (event.key) {
            case 'Enter': {
                if (opened) {
                    // Prevent submit forms
                    event.preventDefault();
                }
                closePopup();
                addChosen(picked);
                break;
            }
            case 'ArrowDown': {
                // Prevent move caret
                event.preventDefault();
                openPopup();
                setPicked(findNextPicked(choices, picked, event.shiftKey));
                break;
            }
            case 'ArrowUp': {
                // Prevent move caret
                event.preventDefault();
                openPopup();
                setPicked(findPrevPicked(choices, picked, event.shiftKey));
                break;
            }
            case 'Backspace': {
                if (chosenPlace !== 'inline' || value || chosen.length === 0) {
                    return;
                }
                setChosen(chosen.slice(0, -1));
                if (chosen.length > 1) {
                    closePopup();
                }
                break;
            }
        }
        if (typeof onKeyDown === 'function') {
            onKeyDown(event);
        }
    }, [
        addChosen, choices, chosen, closePopup, onKeyDown, openPopup,
        opened, picked, chosenPlace, setChosen, setPicked, value,
    ]);
    /** Обработчик нажатия на стрелку открытия попапа */
    var handleArrowClick = useCallback(function () {
        if (opened) {
            closePopup();
        }
        else {
            openPopup();
        }
    }, [opened, closePopup, openPopup]);
    /** Обработчик удаления значения саджеста */
    var handleChosenRemove = useCallback(function (choice, index) {
        if (restorableChosen) {
            markToRemoveChosen([choice], [index]);
        }
        removeChosen([choice]);
    }, [removeChosen, markToRemoveChosen, restorableChosen]);
    /** Обработчик восстановления значения саджеста */
    var handleChosenRestore = useCallback(function (choice, index) {
        restoreChosen([choice], [index]);
    }, [restoreChosen]);
    /** Обработчик прокрутки списка пунктов в попапе */
    var handleChoicesScroll = useCallback(function () {
        var choicesElem = listRef.current;
        if (!choicesElem) {
            return;
        }
        var scrollHeight = choicesElem.scrollHeight, offsetHeight = choicesElem.offsetHeight, scrollTop = choicesElem.scrollTop;
        var progress = scrollTop / (scrollHeight - offsetHeight);
        onChoicesScroll(progress);
    }, [listRef, onChoicesScroll]);
    /** Обработчик выделения пункта */
    var handleChoiceMouseDown = useCallback(function (event, choice) {
        var ctrlKey = event.ctrlKey, metaKey = event.metaKey;
        if (ctrlKey || metaKey) {
            if (hasChoicesSubset(picked, [choice])) {
                removePicked([choice]);
            }
            else {
                addPicked([choice]);
            }
            return;
        }
        // Do not bubble to auto focus root element logic
        event.stopPropagation();
        // Do not affect on focus/blur logic
        event.preventDefault();
        closePopup();
        addChosen([choice]);
    }, [picked, addPicked, removePicked, closePopup, addChosen]);
    var handleSetValueClick = useCallback(function () {
        if (!buttonValue) {
            return;
        }
        addChosen([buttonValue]);
    }, [buttonValue, addChosen]);
    var handlePopupClose = closePopup;
    return {
        // input
        handleInputFocus: handleInputFocus,
        handleInputBlur: handleInputBlur,
        handleInputChange: handleInputChange,
        handleInputMouseDown: handleInputMouseDown,
        handleInputWrapperMouseDown: handleInputWrapperMouseDown,
        handleInputKeyDown: handleInputKeyDown,
        // chosen tags
        handleChosenRemove: handleChosenRemove,
        handleChosenRestore: handleChosenRestore,
        // menu items
        handleChoicesScroll: handleChoicesScroll,
        handleChoiceMouseDown: handleChoiceMouseDown,
        // arrow
        handleArrowClick: handleArrowClick,
        // button
        handleSetValueClick: handleSetValueClick,
        // popup
        handlePopupClose: handlePopupClose,
        // other
        getChosenWithRemoved: getChosenWithRemoved,
        isIndexInRemovedChosenItems: isIndexInRemovedChosenItems,
        // refs
        controlRef: controlRef,
        anchorRef: anchorRef,
        popupRef: popupRef,
        listRef: listRef,
        // state
        focused: focused,
    };
}
